<template>
  <div
    v-if="tabs.length > 0"
    ref="rootEl"
    class="navigation-tabs"
    :class="`navigation-tabs--${size}`"
  >
    <!-- Route mode binds neither the model nor the update listener: with a
         listener present QTabs goes into controlled mode and only *emits* the
         tab picked after a click-navigation instead of activating it, so the
         previous tab would stay highlighted. Without it, QTabs manages the
         active route tab itself. Covered by NavigationTabs.route.spec.ts. -->
    <q-tabs
      v-bind="
        mode === 'route'
          ? {}
          : { modelValue: activeTab, 'onUpdate:modelValue': onTabChange }
      "
      outside-arrows
      dense
      no-caps
      active-color="primary"
      indicator-color="primary"
      align="left"
      :left-icon="toClassicIcon(chevronLeftIcon)"
      :right-icon="toClassicIcon('fa-chevron-right')"
    >
      <component
        :is="mode === 'route' ? QRouteTab : QTab"
        v-for="tab in tabs"
        :key="tab.value"
        :name="tab.value"
        :to="tab.to"
        class="navigation-tabs__tab"
      >
        <div
          class="tw-relative tw-flex tw-items-center tw-gap-1"
          :class="{ 'tw-pr-6': Boolean(tab.badge) }"
        >
          <span
            :ref="(el) => registerTitle(tab.value, el)"
            class="navigation-tabs__title"
          >
            {{ tab.title }}
          </span>

          <!-- Only a clipped title gets the tooltip, so a short tab stays quiet. -->
          <c-tooltip v-if="truncatedTabs.has(tab.value)">
            {{ tab.title }}
          </c-tooltip>

          <span v-if="tab.sublabel" :class="getSublabelClass(tab)">
            {{ tab.sublabel }}
          </span>

          <slot name="suffix" :tab="tab" />

          <c-menu-badge
            v-if="tab.badge"
            :label="tab.badge.label"
            :color="tab.badge.color"
            floating
          />
        </div>
      </component>
    </q-tabs>
  </div>
</template>

<script setup lang="ts">
import { chevronLeftIcon, toClassicIcon } from "common/defaultIcons";
// Imported explicitly (not via the template tag): the Quasar vite plugin only
// auto-imports components it sees as literal tags, which a dynamic `:is`
// escapes.
import { QRouteTab, QTab } from "quasar";
import { useResizeObserver } from "@vueuse/core";
import type { ComponentPublicInstance } from "vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { NavigationTab } from "shared/types";

// Props
const props = withDefaults(
  defineProps<{
    tabs: NavigationTab[];
    /**
     * "query" switches content within the page and persists the selection in
     * the queryParam; "route" navigates to each tab's `to` as given — each tab
     * route owns its own query string (v-model and queryParam are ignored);
     * "model" is the same tab row driven by v-model alone — it neither reads
     * nor writes the URL, for a caller that keeps the selection elsewhere (a
     * drawer whose own id param is the selection).
     */
    mode?: "query" | "route" | "model";
    queryParam?: string;
    /** "sm" is the compact row for inside a drawer; "base" the page row. */
    size?: "base" | "sm";
  }>(),
  {
    mode: "query",
    queryParam: "tab",
    size: "base",
  },
);

// Rendered inside each tab, between the title/sublabel and the badge.
defineSlots<{
  suffix?: (props: { tab: NavigationTab }) => unknown;
}>();

// Router
const route = useRoute();
const router = useRouter();

// Model
const model = defineModel<string>();

const activeTab = computed(() => {
  if (props.mode === "route") {
    const active = props.tabs.find(
      (tab) =>
        tab.to !== undefined && router.resolve(tab.to).name === route.name,
    );
    return active?.value ?? "";
  }

  // Model mode shows whatever the caller holds, even a value that is not (yet)
  // a tab: normalising it to the first tab would move the caller's selection.
  if (props.mode === "model") {
    return model.value ?? props.tabs[0]?.value ?? "";
  }

  const queryValue = route.query[props.queryParam];

  if (
    typeof queryValue === "string" &&
    props.tabs.some((tab) => tab.value === queryValue)
  ) {
    return queryValue;
  }

  return props.tabs[0]?.value ?? "";
});

watch(
  activeTab,
  (value) => {
    // Route mode never touches the model or the query string — q-route-tab
    // handles the navigation and active state itself. Model mode only writes
    // the model on a click (onTabChange).
    if (props.mode === "route" || props.mode === "model") {
      return;
    }

    model.value = value;

    const queryValue = route.query[props.queryParam];
    const isValidTab = props.tabs.some((tab) => tab.value === queryValue);

    // If no query param is set or the query param is invalid, update the URL to the active tab
    if (
      props.tabs.length > 0 &&
      (queryValue === undefined || isValidTab === false)
    ) {
      router.replace({
        query: {
          ...route.query,
          [props.queryParam]: value,
        },
      });
    }
  },
  { immediate: true },
);

// A title wider than its max-width is clipped with an ellipsis; those tabs get
// a tooltip with the full title. Measured, since CSS cannot tell us.
const rootEl = ref<HTMLElement | null>(null);
const titleEls = new Map<string, HTMLElement>();
const truncatedTabs = ref(new Set<string>());

const registerTitle = (
  value: string,
  el: Element | ComponentPublicInstance | null,
): void => {
  if (el instanceof HTMLElement) {
    titleEls.set(value, el);
    return;
  }

  titleEls.delete(value);
};

const measureTruncation = (): void => {
  const clipped = new Set<string>();

  titleEls.forEach((el, value) => {
    if (el.scrollWidth > el.clientWidth) {
      clipped.add(value);
    }
  });

  truncatedTabs.value = clipped;
};

onMounted(measureTruncation);
useResizeObserver(rootEl, measureTruncation);

watch(
  () => props.tabs,
  async () => {
    await nextTick();
    measureTruncation();
  },
  { deep: true },
);

const getSublabelClass = (tab: NavigationTab): string => {
  return activeTab.value === tab.value
    ? "tw-text-aqua-500"
    : "tw-text-gray-500";
};

// Bound in query and model mode; route mode navigates via the q-route-tabs.
const onTabChange = (value: string | number) => {
  if (props.mode === "model") {
    model.value = String(value);
    return;
  }

  router.push({
    query: {
      ...route.query,
      [props.queryParam]: value,
    },
  });
};
</script>

<style lang="scss" scoped>
.navigation-tabs {
  @apply tw-border-b tw-border-gray-100 tw-font-medium;

  // Room for a title like "Levensbeschouwing en maatschappij" before it is
  // clipped; a longer one shows in full in the tooltip.
  &__title {
    @apply tw-inline-block tw-max-w-48 tw-truncate;
  }

  &--base &__tab {
    @apply tw-py-4;
  }

  &--sm &__tab {
    @apply tw-min-h-7 tw-py-2;
  }
}
</style>
